import React from "react";
import { observer } from "mobx-react-lite";
import { Form } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { emptyValue } from "utils/emptyValue";
import { formatAmount, formatDate } from "utils/localizations";
import type PaymentBankTransferDetailViewModel from "viewModels/finance/payments/bankTransfer/paymentBankTransferDetailViewModel";
import Section from "views/common/section";
import { FormLabelWithTooltip } from "controls/formLabelWithTooltip";

export interface BaseInfoProps {
  vm: PaymentBankTransferDetailViewModel;
}

const BaseInfo = observer((props: BaseInfoProps) => {
  const { vm } = props;

  return (
    <Section title={vm.translate("baseInfoTitle")} context>
      <Form.Row>
        <Form.Group as={Col} sm={4}>
          <FormLabelWithTooltip tooltipText={vm.translate("postingDateTooltip")}>
            {vm.translate("postingDate")}
          </FormLabelWithTooltip>
          <FakeInput target={vm.item} property="postingDate" valueFormatter={value => (value ? formatDate(value) : emptyValue)} />
        </Form.Group>
        <Form.Group as={Col} sm={4}>
          <Form.Label>{vm.translate("debitingDate")}</Form.Label>
          <FakeInput
            target={vm.item}
            property="debitingDate"
            valueFormatter={value => (value ? formatDate(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} sm={4}>
          <Form.Label>{vm.translate("currencyDate")}</Form.Label>
          <FakeInput
            target={vm.item}
            property="currencyDate"
            valueFormatter={value => (value ? formatDate(value) : emptyValue)}
          />
        </Form.Group>
      </Form.Row>
      <Form.Row>
        <Form.Group as={Col} sm={4}>
          <Form.Label>{vm.translate("bankTransactionType")}</Form.Label>
          <FakeInput target={vm.item} property="bankTransactionType" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} sm={4}>
          <Form.Label>{vm.translate("amount")}</Form.Label>
          <FakeInput
            className={`text-${vm.isAmountPositive ? "success" : "danger"}`}
            target={vm.item}
            property="amount"
            valueFormatter={value => (value ? <strong>{formatAmount(value)}</strong> : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} sm={4}>
          <Form.Label>{vm.translate("operationType")}</Form.Label>
          <FakeInput
            target={vm.item}
            property="operationType"
            valueFormatter={value => vm.translateOperationType(value) ?? emptyValue}
          />
        </Form.Group>
      </Form.Row>
      <Form.Row>
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translate("paymentTemplateId")}</Form.Label>
          <FakeInput
            target={vm.item}
            property="paymentTemplateId"
            valueFormatter={value => vm.translatePaymentTemplate(value) ?? emptyValue}
          />
        </Form.Group>
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translate("paymentFeeType")}</Form.Label>
          <FakeInput target={vm.item} property="paymentFeeType" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
      </Form.Row>
      <Form.Row>
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translate("messageForRecipient")}</Form.Label>
          <FakeInput target={vm.item} property="messageForRecipient" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translate("additionalInformation")}</Form.Label>
          <FakeInput target={vm.item} property="additionalInformation" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
      </Form.Row>
    </Section>
  );
});

export default BaseInfo;
